import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminDeleteButton } from '../components/AdminDeleteButton';

const empty = { name: '', code: '', address: '', contact_name: '', contact_email: '', contact_phone: '', currency: 'USD' };

export default function CompaniesPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetch = () => api.get('/companies').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    try {
      if (editing) { await api.put(`/companies/${editing}`, form); toast.success('Company updated'); }
      else { await api.post('/companies', form); toast.success('Company created'); }
      setOpen(false); setForm(empty); setEditing(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const toggleStatus = async (c) => {
    const s = c.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/companies/${c.company_id}/status`, { status: s });
    toast.success(`Company ${s}`); fetch();
  };

  const remove = async (id) => {
    try { const r = await api.delete(`/companies/${id}`); toast.success('Company and all its data deleted'); fetch(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const edit = (c) => { setForm({ name: c.name, code: c.code, address: c.address || '', contact_name: c.contact_name || '', contact_email: c.contact_email || '', contact_phone: c.contact_phone || '', currency: c.currency || 'USD' }); setEditing(c.company_id); setOpen(true); };
  const filtered = data.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="companies-page">
      <div className="page-header">
        <div><h1 className="page-title">Companies</h1><p className="text-sm text-slate-500 mt-1">{data.length} total companies</p></div>
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-company-btn"><Plus size={16} className="mr-1.5" />Add Company</Button>
      </div>

      <div className="mb-4 relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="search-companies" />
      </div>

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Currency</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No companies found</TableCell></TableRow> :
            filtered.map(c => (
              <TableRow key={c.company_id} className="hover:bg-slate-50/50" data-testid={`company-row-${c.company_id}`}>
                <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                <TableCell className="text-slate-600 font-mono text-xs">{c.code}</TableCell>
                <TableCell><span className="text-sm text-slate-600">{c.contact_name}</span>{c.contact_email && <span className="block text-xs text-slate-400">{c.contact_email}</span>}</TableCell>
                <TableCell className="text-sm text-slate-600 font-mono">{({'INR':'₹','USD':'$','EUR':'€','AUD':'A$','GBP':'£'})[c.currency] || c.currency || '₹'} {c.currency || 'INR'}</TableCell>
                <TableCell><button onClick={() => toggleStatus(c)} className={`status-badge cursor-pointer status-${c.status}`} data-testid={`toggle-status-${c.company_id}`}>{c.status}</button></TableCell>
                <TableCell><div className="flex items-center gap-1"><Button variant="ghost" size="sm" onClick={() => edit(c)} data-testid={`edit-company-${c.company_id}`}><Pencil size={14} /></Button><AdminDeleteButton itemName={c.name} description={`Permanently delete "${c.name}" AND all its users, sites, machines, products, suppliers, categories and cycle data. This cannot be undone.`} onConfirm={() => remove(c.company_id)} testId={`delete-company-${c.company_id}`} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Company' : 'Add Company'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-4 mt-2">
            <div className="form-field"><Label>Company Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-50" data-testid="company-name-input" /></div>
            <div className="form-field"><Label>Company Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="bg-slate-50" data-testid="company-code-input" /></div>
            <div className="form-field col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="bg-slate-50" data-testid="company-address-input" /></div>
            <div className="form-field"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Contact Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field">
              <Label>Currency *</Label>
              <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                <SelectTrigger className="bg-slate-50" data-testid="company-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-company-btn">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
